/**
 * 安全周报计算工具函数
 */

import { DBO_V } from '@op/utils/format';

/**
 * 计算分位值
 * @param data 数据数组
 * @param percentile 分位值 (0-1)，如 0.5 表示 50 分位
 * @returns 分位值
 */
export const calculateQuantile = (data: number[], percentile: number): number => {
  if (!data.length) return 0;
  const sorted = [...data].sort((a, b) => a - b);
  const index = percentile * (sorted.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sorted[lower];
  const weight = index - lower;
  return sorted[lower] * (1 - weight) + sorted[upper] * weight;
};

/**
 * 计算波动贡献率
 * 公式: (A类型本周声量-上周声量) / |安全舆情本周声量-上周声量|
 * @param currentValue 当前值
 * @param previousValue 上期值
 * @param totalCurrentValue 总当前值
 * @param totalPreviousValue 总上期值
 * @returns 波动贡献率
 */
export const calculateContribution = (
  currentValue: number,
  previousValue: number,
  totalCurrentValue: number,
  totalPreviousValue: number
): number => {
  const itemDelta = currentValue - previousValue;
  const totalDelta = Math.abs(totalCurrentValue - totalPreviousValue);
  if (totalDelta === 0) return 0;
  return itemDelta / totalDelta;
};

/**
 * 判断四象限位置
 * @param mention 声量 (X轴)
 * @param intensity 互动强度 (Y轴)
 * @param xThreshold 声量阈值（60分位）
 * @param yThreshold 互动强度阈值（50分位）
 * @returns 象限编号 (1-4)
 * 
 * 象限定义（与 QUADRANT_CONFIG 对应）：
 * Q1: 扩散中 Spreading（右下）- 高声量 + 低互动 → 需关注
 * Q2: 已发酵 Escalated（右上）- 高声量 + 高互动 → 必须介入
 * Q3: 情绪放大 Emotion Amplifying（左上）- 低声量 + 高互动 → 重点关注
 * Q4: 稳定 Stable（左下）- 低声量 + 低互动 → 观察即可
 */
export const getQuadrant = (
  mention: number,
  intensity: number,
  xThreshold: number,
  yThreshold: number
): 1 | 2 | 3 | 4 => {
  const isHighMention = mention >= xThreshold;
  const isHighIntensity = intensity >= yThreshold;

  if (isHighMention && isHighIntensity) return 2;  // 右上：已发酵 Escalated
  if (isHighMention && !isHighIntensity) return 1; // 右下：扩散中 Spreading
  if (!isHighMention && isHighIntensity) return 3; // 左上：情绪放大 Emotion Amplifying
  return 4;                                        // 左下：稳定 Stable
};

/**
 * 识别突增关键词
 * 定义：当周出现但之前一个月没出现过的词
 * @param currentKeywords 当前关键词列表
 * @param historicalKeywords 历史关键词列表
 * @returns 突增关键词列表
 */
export const identifyNewKeywords = (
  currentKeywords: string[],
  historicalKeywords: string[]
): string[] => {
  const historicalSet = new Set(historicalKeywords);
  return currentKeywords.filter(keyword => !historicalSet.has(keyword));
};

/**
 * 根据变化率计算气泡大小
 * @param changeRate 变化率
 * @param minSize 最小尺寸
 * @param maxSize 最大尺寸
 * @returns 气泡大小（px）
 */
export const calculateBubbleSize = (
  changeRate: number,
  minSize = 14,
  maxSize = 40
): number => {
  const absRate = Math.abs(changeRate);
  // 变化率 0-200% 映射到 minSize-maxSize
  const normalizedRate = Math.min(absRate, 2);
  const size = minSize + (normalizedRate / 2) * (maxSize - minSize);
  return Math.round(size);
};

/**
 * 格式化贡献率文本参数
 */
export interface IContributionTextParams {
  /** 类目名称 */
  categoryName: string;
  /** 变化率（小数形式，如 0.1 表示 10%） */
  changeRate: number;
  /** 贡献率（小数形式，如 0.3 表示 30%） */
  contributionRate: number;
  /** 可选的标签前缀，如 "Channel"、"Language" */
  labelPrefix?: string;
  /** formatMessage 函数 */
  formatMessage: (descriptor: { id: string }, values?: Record<string, any>) => string;
  /** DBO_V.ratio 格式化函数 */
  ratioFormatter?: (value: number, fallback?: string, decimal?: number) => string;
}

/**
 * 生成波动贡献率描述文本
 * 统一处理：
 * 1. 检查 changeRate 是否为 0，为 0 则返回空字符串
 * 2. 格式化 changeRate，正数添加 + 号
 * 3. 格式化 contributionRate
 * 4. 返回格式化后的文本
 *
 * @param params 参数对象
 * @returns 格式化后的文本，如果 changeRate 为 0 则返回空字符串
 */
export const formatContributionText = (params: IContributionTextParams): string => {
  const {
    categoryName,
    changeRate,
    contributionRate,
    labelPrefix = '',
    formatMessage,
    ratioFormatter = DBO_V.ratio,
  } = params;

  // 如果变化率为 0，不显示
  if (changeRate === 0) return '';

  // 格式化变化率，正数时添加 + 号
  const changeRateValue = ratioFormatter(changeRate, '-', 2);
  const changeRateText =
    changeRate > 0 && changeRateValue !== '-'
      ? `+${changeRateValue}`
      : changeRateValue;

  // 格式化贡献率
  const contributionRateText = ratioFormatter(contributionRate, '-', 2);

  return formatMessage(
    {
      id: 'Largest change contributor this week: {B} {A}. {B} {A}: {C}  change ({D} contribution).',
    },
    {
      A: categoryName,
      B: labelPrefix,
      C: changeRateText,
      D: contributionRateText,
    },
  );
};
